import React, { useState } from 'react';
import { Upload, X, Plus, Eye, Edit2 } from 'lucide-react';
import apiService from '@/services/api';

interface Section {
  id: number;
  page_slug: string;
  section_key: string;
  section_type: string;
  title?: string;
  subtitle?: string;
  body: string;
  display_order: number;
}

interface PageBuilderProps {
  section: Section;
  onSave: (sectionId: number, data: any) => Promise<void>;
  onCancel: () => void;
}

const PageBuilder: React.FC<PageBuilderProps> = ({ section, onSave, onCancel }) => {
  const [editData, setEditData] = useState({
    title: section.title || '',
    subtitle: section.subtitle || '',
    body: section.body || '[]'
  });
  const [previewMode, setPreviewMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Parse body as JSON
  let parsedBody: any = [];
  try {
    parsedBody = typeof editData.body === 'string' ? JSON.parse(editData.body) : editData.body;
  } catch (e) {
    parsedBody = [];
  }

  const handleSave = async () => {
    try {
      setSaving(true);
      // Validate JSON
      JSON.parse(editData.body);
      await onSave(section.id, editData);
    } catch (err: any) {
      alert('Invalid JSON: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const addItem = (type: string) => {
    try {
      const current = JSON.parse(editData.body);
      let newItem: any = {};

      switch (section.section_type) {
        case 'hero':
          newItem = { title: 'Hero Title', subtitle: 'Hero Subtitle', image: '', cta: 'Get Started' };
          break;
        case 'cards':
          newItem = { icon: '📦', title: 'New Card', description: 'Card description' };
          break;
        case 'testimonials':
          newItem = { quote: 'Customer quote', author: 'Author Name', role: 'Role' };
          break;
        case 'stats':
          newItem = { value: '100%', label: 'Label' };
          break;
        case 'features':
          newItem = { icon: '✨', title: 'Feature', description: 'Feature description' };
          break;
        case 'steps':
          newItem = { number: current.length + 1, title: 'Step', description: 'Description' };
          break;
        case 'faq':
          newItem = { question: 'Question?', answer: 'Answer here' };
          break;
        case 'cta':
          newItem = { text: 'Button Text', url: '/', variant: 'primary' };
          break;
      }

      const updated = Array.isArray(current) ? [...current, newItem] : current;
      setEditData(prev => ({ ...prev, body: JSON.stringify(updated, null, 2) }));
    } catch (e) {
      alert('Error adding item');
    }
  };

  const removeItem = (index: number) => {
    try {
      const current = JSON.parse(editData.body);
      const updated = Array.isArray(current) ? current.filter((_: any, i: number) => i !== index) : current;
      setEditData(prev => ({ ...prev, body: JSON.stringify(updated, null, 2) }));
    } catch (e) {
      alert('Error removing item');
    }
  };

  const updateItem = (index: number, field: string, value: any) => {
    try {
      const current = JSON.parse(editData.body);
      if (Array.isArray(current) && current[index]) {
        current[index][field] = value;
        setEditData(prev => ({ ...prev, body: JSON.stringify(current, null, 2) }));
      }
    } catch (e) {
      console.error('Error updating item', e);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{section.section_key}</h2>
            <p className="text-sm text-gray-600">Type: {section.section_type}</p>
          </div>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-gray-50 border-b border-gray-200 flex gap-4 px-6 sticky top-20">
          <button
            onClick={() => setPreviewMode(false)}
            className={`py-3 px-4 font-medium transition-colors ${
              !previewMode
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Edit2 className="w-4 h-4 inline mr-2" />
            Edit
          </button>
          <button
            onClick={() => setPreviewMode(true)}
            className={`py-3 px-4 font-medium transition-colors ${
              previewMode
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Eye className="w-4 h-4 inline mr-2" />
            Preview
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {!previewMode ? (
            <div className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  value={editData.title}
                  onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Section title"
                />
              </div>

              {/* Subtitle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                <textarea
                  value={editData.subtitle}
                  onChange={(e) => setEditData(prev => ({ ...prev, subtitle: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Section subtitle"
                  rows={2}
                />
              </div>

              {/* Items Editor */}
              {(section.section_type === 'cards' ||
                section.section_type === 'testimonials' ||
                section.section_type === 'stats' ||
                section.section_type === 'features' ||
                section.section_type === 'steps' ||
                section.section_type === 'faq' ||
                section.section_type === 'cta') && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-medium text-gray-700">Items</label>
                    <button
                      onClick={() => addItem(section.section_type)}
                      className="px-3 py-1 bg-teal-600 text-white rounded text-sm hover:bg-teal-700 transition-colors flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Item
                    </button>
                  </div>

                  <div className="space-y-4">
                    {parsedBody.map && parsedBody.map((item: any, index: number) => (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-700">Item {index + 1}</span>
                          <button
                            onClick={() => removeItem(index)}
                            className="p-1 hover:bg-red-100 text-red-600 rounded transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Dynamic fields based on item type */}
                        {Object.entries(item).map(([key, value]: [string, any]) => (
                          <div key={key}>
                            <label className="block text-xs font-medium text-gray-600 uppercase mb-1">
                              {key}
                            </label>
                            {key === 'image' || key.includes('url') ? (
                              <input
                                type="text"
                                value={value || ''}
                                onChange={(e) => updateItem(index, key, e.target.value)}
                                placeholder="Image URL"
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                              />
                            ) : key === 'description' || key === 'answer' || key === 'quote' ? (
                              <textarea
                                value={value || ''}
                                onChange={(e) => updateItem(index, key, e.target.value)}
                                rows={2}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                              />
                            ) : (
                              <input
                                type={typeof value === 'number' ? 'number' : 'text'}
                                value={value || ''}
                                onChange={(e) => updateItem(index, key, e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Raw JSON Editor */}
              {!['cards', 'testimonials', 'stats', 'features', 'steps', 'faq', 'cta'].includes(
                section.section_type
              ) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Content (JSON)</label>
                  <textarea
                    value={editData.body}
                    onChange={(e) => setEditData(prev => ({ ...prev, body: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono text-sm"
                    rows={10}
                    placeholder="Enter JSON content..."
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{editData.title}</h3>
                <p className="text-gray-600">{editData.subtitle}</p>
              </div>

              {Array.isArray(parsedBody) && parsedBody.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Items Preview:</h4>
                  <div className="grid grid-cols-1 gap-3">
                    {parsedBody.map((item: any, i: number) => (
                      <div key={i} className="bg-gray-50 p-3 rounded border border-gray-200">
                        <pre className="text-xs whitespace-pre-wrap break-words">
                          {JSON.stringify(item, null, 2)}
                        </pre>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {typeof parsedBody === 'string' && (
                <div className="bg-gray-50 p-4 rounded border border-gray-200">
                  <pre className="text-sm whitespace-pre-wrap break-words">{parsedBody}</pre>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-6 py-2 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-50 rounded-lg transition-colors"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PageBuilder;
